import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import type { authClient } from "../lib/auth-client";
import { ToastProvider } from "../components/ui";
import { ConsentBanner } from "../components/ConsentBanner";

// The signed-in chrome (sidebar, menus, billing banners) is a separate chunk,
// so signed-out screens paint without downloading it.
const loadAppShell = () => import("../components/shell/AppShell");
const AppShell = lazy(loadAppShell);

interface RouterContext {
  queryClient: QueryClient;
  authClient: typeof authClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    if (
      location.pathname === "/sign-in" ||
      location.pathname === "/auth/callback"
    ) {
      return null;
    }
    const session = await context.authClient.getSession();
    if (!session.data) {
      throw redirect({ to: "/sign-in" });
    }
    // Fetch the shell alongside the route's own chunk, not after it.
    void loadAppShell();
    return { session: session.data };
  },
  component: RootLayout,
});

const BARE_ROUTES = new Set(["/sign-in", "/auth/callback"]);

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <>
      {/* Signed-out screens own the whole viewport: no app chrome. */}
      {BARE_ROUTES.has(pathname) ? (
        <ToastProvider position="top-right">
          <Outlet />
        </ToastProvider>
      ) : (
        <Suspense fallback={null}>
          <AppShell />
        </Suspense>
      )}
      <ConsentBanner pathname={pathname} />
    </>
  );
}
